import { css, type SerializedStyles } from '@emotion/react';
import isObject from 'lodash/isObject';
import type { CSSProperties } from 'react';

import type { Breakpoint, DeviceType } from '../themes/types';
import { deviceMediaQueries, mq, type screenSizeMediaQueries } from '../utilities/mediaQueries';

type CSSValue = CSSProperties[keyof CSSProperties];
type ResponsiveValue<T extends CSSValue> = T | Partial<Record<Breakpoint, T>> | Partial<Record<DeviceType, T>>;

type GenerateResponsiveStylesParams<T, V extends CSSValue> = {
  value: ResponsiveValue<V>;
  theme?: T;
} & (
  | { cssProp: keyof CSSProperties; styleFn?: never }
  | {
      cssProp?: never;
      styleFn: (props: { value: V; theme?: T }) => SerializedStyles | undefined;
    }
);

const isDeviceInput = (value: object): boolean => Object.keys(value).some((key) => key in deviceMediaQueries);

const getMediaQuery = (key: string, isDevice: boolean): string =>
  isDevice ? deviceMediaQueries[key as DeviceType] : mq[`>=${key}` as keyof typeof screenSizeMediaQueries];

export const generateResponsiveStyles = <T, V extends CSSValue>({
  value,
  theme,
  cssProp,
  styleFn,
}: GenerateResponsiveStylesParams<T, V>) => {
  if (isObject(value)) {
    const isDevice = isDeviceInput(value);
    const entries = isDevice
      ? Object.entries(value)
      : Object.entries(sortResponsiveObjectByKeys(value as Record<Breakpoint, V>));

    return entries.map(([key, _value]) =>
      css({
        [getMediaQuery(key, isDevice)]: {
          ...(cssProp ? { [cssProp]: _value } : styleFn({ value: _value, theme })),
        },
      }),
    );
  }

  return cssProp ? css({ [cssProp]: value }) : styleFn({ value: value, theme });
};

const sortResponsiveObjectByKeys = <T extends Record<Breakpoint, CSSProperties[keyof CSSProperties]>>(obj: T): T => {
  const order: (keyof T)[] = ['xs', 'sm', 'md', 'lg', 'xl'];

  const sortedObject = {} as T;
  order.forEach((breakpoint) => {
    if (breakpoint in obj) {
      sortedObject[breakpoint] = obj[breakpoint];
    }
  });

  return sortedObject;
};
